import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  BrowserCacheLocation,
  PublicClientApplication
} from '@azure/msal-browser';
import { environment } from '../environments/test.environment';

@Injectable({ providedIn: 'root' })
export class MicrosoftSsoService {
  private readonly browser: boolean;
  private clientPromise: Promise<PublicClientApplication> | null = null;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.browser = isPlatformBrowser(platformId);
  }

  isSupportedEmail(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    const separatorIndex = normalized.lastIndexOf('@');
    if (separatorIndex < 0) return false;

    const domain = normalized.slice(separatorIndex + 1);
    return environment.microsoftAuth.allowedEmailDomains.some(
      allowedDomain => domain === allowedDomain.toLowerCase()
    );
  }

  async signIn(loginHint: string): Promise<void> {
    const client = await this.getClient();
    await client.loginRedirect({
      scopes: environment.microsoftAuth.loginScopes,
      loginHint: loginHint.trim(),
      redirectStartPage: this.getRedirectUri()
    });
  }

  async handleRedirectAndGetIdToken(): Promise<string | null> {
    if (!this.browser) return null;

    const client = await this.getClient();
    const result = await client.handleRedirectPromise();
    if (!result) return null;

    client.setActiveAccount(result.account);
    if (!result.idToken) {
      throw new Error('Microsoft sign-in did not return an ID token.');
    }

    return result.idToken;
  }

  async logout(): Promise<void> {
    if (!this.browser) return;

    const client = await this.getClient();
    const account = client.getActiveAccount() ?? client.getAllAccounts()[0];
    await client.logoutRedirect({
      account,
      postLogoutRedirectUri: this.getRedirectUri()
    });
  }

  private getClient(): Promise<PublicClientApplication> {
    if (!this.browser) {
      return Promise.reject(new Error('Microsoft sign-in is only available in a browser.'));
    }

    if (!this.clientPromise) {
      const client = new PublicClientApplication({
        auth: {
          clientId: environment.microsoftAuth.clientId,
          authority: environment.microsoftAuth.authority,
          redirectUri: this.getRedirectUri(),
          postLogoutRedirectUri: this.getRedirectUri()
        },
        cache: {
          cacheLocation: BrowserCacheLocation.SessionStorage
        }
      });

      this.clientPromise = client.initialize().then(() => client);
    }

    return this.clientPromise;
  }

  private getRedirectUri(): string {
    return environment.microsoftAuth.redirectUri
      ?? `${window.location.origin}${environment.microsoftAuth.redirectPath}`;
  }
}
